/**
 * formCodes — the ONE mapping from schema form_id to the legacy
 * intake_forms.form_code convention, plus the supplementary-form registry.
 *
 * TRAP (ledger, standing): intake_forms.form_code holds LEGACY codes
 * ('47SP-applicant'), NOT schema form_ids. Every lookup that filters on
 * form_code must go through this module.
 *
 * SUPPLEMENTARY forms (Form 80) get their OWN intake_forms row per
 * (matter, contact) alongside the primary intake row. Every
 * "latest intake row for this contact" lookup that means the PRIMARY intake
 * must exclude supplementary codes — otherwise a fresher Form 80 save would
 * shadow the real intake row (progress, PDF actions, gates would all read the
 * wrong form). Use excludeSupplementary() on those queries.
 */

/** form_code for the applicant's Form 80 supplementary intake row. */
export const F80_FORM_CODE = 'F80-applicant';

/**
 * Every supplementary form_code (excluded from primary-intake lookups).
 * KEEP IN SYNC with the hardcoded exclusion in
 * supabase/functions/send-form-888-email/index.ts (Deno cannot import this
 * module).
 */
export const SUPPLEMENTARY_FORM_CODES = [F80_FORM_CODE] as const;

/**
 * PostgREST filter fragment for excluding supplementary rows:
 *   query.not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
 */
export const SUPPLEMENTARY_FORM_CODES_SQL = `(${SUPPLEMENTARY_FORM_CODES.join(',')})`;

/**
 * Map form_id → form_code and form_version for the intake_forms table.
 * These codes match the v1 IntakeForm convention so staff can cross-reference.
 * (Moved from IntakeFormV2 so the Form 80 tab and scripts share one copy.)
 */
export function deriveFormCodeVersion(formId: string): {
  form_code: string;
  form_version: string;
} {
  const mapping: Record<string, string> = {
    '47SP_820_onshore_stage1': '47SP-applicant',
    '47SP_309_offshore_stage1': '47SP-applicant',
    '47SP_300_PMV': '47SP-applicant',
    // Stage 2 lives on the SAME row as stage 1: the latch endpoint writes
    // stage1/stage2_completed_at onto one row, and CreateMatter creates
    // '47SP-applicant' for every partner subclass including 801/100. A
    // distinct stage-2 code would orphan every stage-2 matter's saved data.
    '47SP_stage2': '47SP-applicant',
    '40SP_sponsor': '40SP-sponsor',
    '1419_tourist': '1419-visitor-tourist',
    '1418_family_sponsored': '1418-visitor-family',
    '147_nz_family': '147-461-nz-family',
    '1149_family_visitor_sponsor': '1149-family-visitor-sponsor',
    // ADI's own light Inviter form for the 600 tourist stream (Wave 5). No
    // Home Affairs form number exists for an inviter, so the code follows the
    // hyphenated convention rather than a Department form number.
    '600_tourist_inviter': '600-tourist-inviter',
    // CreateMatter has always written the hyphenated code for 020.
    '1006_bridging_visa_b': '1006-bridging-visa-b',
    'F80_character': F80_FORM_CODE,
  };
  return {
    form_code: mapping[formId] ?? formId,
    form_version: '2026.04',
  };
}
